/**
 * Granice Ceny - Panel Administracyjny
 * Kontrola minimalnej marży przy sprzedaży w POS
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { priceLimitsService } from '../../services/priceLimitsService';
import { productService } from '../../services/productService';

const PriceLimitsTab = ({ initialLocationId = 5 }) => {
  // Wybór lokalizacji
  const [locationId, setLocationId] = useState(initialLocationId);
  const [locations, setLocations] = useState([]);

  // Ustawienia globalne
  const [settings, setSettings] = useState({
    min_margin_percent: 0,
    enabled: false,
    allow_override_with_permission: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Wyjątki
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);

  // Załaduj lokalizacje
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await api.get('/locations');
        const data = response.data;
        if (data.success && data.data) {
          setLocations(data.data);
        }
      } catch (err) {
        console.error('Błąd ładowania lokalizacji:', err);
      }
    };
    loadLocations();
  }, []);

  // Dodawanie wyjątku
  const [showAddException, setShowAddException] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [newException, setNewException] = useState({
    product_id: null,
    product_name: '',
    min_margin_percent: 0,
    reason: ''
  });

  // Błędy
  const [error, setError] = useState('');

  // Załaduj ustawienia
  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await priceLimitsService.getSettings(locationId);
      if (response.success && response.data) {
        // Konwertuj 1/0 z bazy na true/false
        setSettings({
          min_margin_percent: response.data.min_margin_percent || 0,
          enabled: response.data.enabled === 1 || response.data.enabled === true,
          allow_override_with_permission: response.data.allow_override_with_permission === 1 || response.data.allow_override_with_permission === true
        });
      }
    } catch (err) {
      setError('Błąd ładowania ustawień');
    } finally {
      setSettingsLoading(false);
    }
  }, [locationId]);

  // Załaduj wyjątki
  const loadExceptions = useCallback(async () => {
    setExceptionsLoading(true);
    try {
      const response = await priceLimitsService.getExceptions(locationId);
      if (response.success) {
        setExceptions(response.data || []);
      }
    } catch (err) {
      setError('Błąd ładowania wyjątków');
    } finally {
      setExceptionsLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    loadSettings();
    loadExceptions();
  }, [loadSettings, loadExceptions]);

  // Zapisz ustawienia
  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    setSettingsSaved(false);
    try {
      const response = await priceLimitsService.saveSettings(locationId, settings);
      if (response.success) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
      } else {
        setError(response.message || 'Błąd zapisywania');
      }
    } catch (err) {
      setError('Błąd zapisywania ustawień');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Wyszukaj produkty
  const handleProductSearch = async (query) => {
    setProductSearch(query);
    if (query.length < 2) {
      setProductSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const result = await productService.searchProducts({ query, limit: 10 });
      setProductSearchResults(result.products || []);
    } catch (err) {
      console.error('Błąd wyszukiwania:', err);
      setProductSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Wybierz produkt
  const handleSelectProduct = (product) => {
    setNewException({
      ...newException,
      product_id: product.id,
      product_name: product.nazwa
    });
    setProductSearch(product.nazwa);
    setProductSearchResults([]);
  };

  // Dodaj wyjątek
  const handleAddException = async () => {
    if (!newException.product_id) {
      setError('Wybierz produkt');
      return;
    }

    try {
      const response = await priceLimitsService.addException(
        locationId,
        newException.product_id,
        newException.min_margin_percent,
        newException.reason
      );

      if (response.success) {
        loadExceptions();
        setShowAddException(false);
        setNewException({ product_id: null, product_name: '', min_margin_percent: 0, reason: '' });
        setProductSearch('');
      } else {
        setError(response.message || 'Błąd dodawania wyjątku');
      }
    } catch (err) {
      setError('Błąd dodawania wyjątku');
    }
  };

  // Usuń wyjątek
  const handleDeleteException = async (exceptionId) => {
    if (!window.confirm('Czy na pewno usunąć ten wyjątek?')) return;

    try {
      const response = await priceLimitsService.deleteException(locationId, exceptionId);
      if (response.success) {
        loadExceptions();
      } else {
        setError(response.message || 'Błąd usuwania');
      }
    } catch (err) {
      setError('Błąd usuwania wyjątku');
    }
  };

  return (
    <div className="admin-table-wrap" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        @media (max-width: 600px) {
          .pl-settings-grid { grid-template-columns: 1fr !important; }
          .pl-loc-row { flex-wrap: wrap !important; gap: 0.5rem !important; }
          .pl-loc-row select { min-width: 0 !important; width: 100% !important; flex: 1 1 100% !important; }
          .pl-exception-form { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 601px) and (max-width: 900px) {
          .pl-settings-grid { grid-template-columns: 1fr 1fr !important; }
          .pl-exception-form { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
      {/* Nagłówek */}
      <div style={{
        backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)' ,
        padding: '1rem 1.5rem',
        borderRadius: '8px 8px 0 0',
        marginBottom: '1rem'
      }}>
        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
          🚫 Granice Ceny (Minimalna Marża)
        </h4>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
          Kontroluj minimalną marżę przy sprzedaży w POS - zabezpiecz się przed sprzedażą poniżej ceny zakupu
        </p>
      </div>

      {/* Wybór lokalizacji */}
      <div className="pl-loc-row" style={{
        backgroundColor: 'var(--info-light)',
        border: '1px solid var(--info-light)',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <span style={{ fontWeight: '600', color: 'var(--info)' }}>📍 Lokalizacja:</span>
        <select
          value={locationId}
          onChange={(e) => setLocationId(parseInt(e.target.value))}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text)',
            minWidth: '250px'
          }}
        >
          {locations.length === 0 ? (
            <option value={5}>Ładowanie lokalizacji...</option>
          ) : (
            locations.map(loc => (
              <option key={loc.id} value={loc.id}>
                {loc.nazwa || loc.name} (ID: {loc.id})
              </option>
            ))
          )}
        </select>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Ustawienia granic ceny są osobne dla każdej lokalizacji
        </span>
      </div>

      {/* Błąd */}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none' }}>✕</button>
        </div>
      )}

      {/* Sekcja 1: Ustawienia globalne */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '1.25rem',
        marginBottom: '1rem'
      }}>
        <h5 style={{
          margin: '0 0 1rem',
          fontSize: '0.95rem',
          fontWeight: '600',
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          ⚙️ Ustawienia Globalne
        </h5>

        <div className="pl-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          {/* Włącz/Wyłącz */}
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
              Status kontroli
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
              backgroundColor: settings.enabled ? 'var(--success-light)' : 'var(--bg)',
              borderRadius: '4px',
              border: `1px solid ${settings.enabled ? 'var(--success-light)' : 'var(--border)'}`
            }}>
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: '500', color: settings.enabled ? 'var(--success)' : 'var(--text-muted)' }}>
                {settings.enabled ? '✓ Włączona' : 'Wyłączona'}
              </span>
            </label>
          </div>

          {/* Minimalna marża % */}
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
              Minimalna marża dla wszystkich produktów
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={settings.min_margin_percent}
                onChange={(e) => setSettings({ ...settings, min_margin_percent: parseFloat(e.target.value) || 0 })}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              />
              <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>%</span>
            </div>
            <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              Przykład: przy 5% marży, produkt kupiony za 100 zł nie może być sprzedany taniej niż 105 zł
            </small>
          </div>

          {/* Pozwól na obejście */}
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
              Obejście z uprawnieniami
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
              backgroundColor: settings.allow_override_with_permission ? 'var(--warning-light)' : 'var(--bg)',
              borderRadius: '4px',
              border: `1px solid ${settings.allow_override_with_permission ? 'var(--warning)' : 'var(--border)'}`
            }}>
              <input
                type="checkbox"
                checked={settings.allow_override_with_permission}
                onChange={(e) => setSettings({ ...settings, allow_override_with_permission: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.85rem', color: settings.allow_override_with_permission ? 'var(--warning)' : 'var(--text-muted)' }}>
                {settings.allow_override_with_permission ? 'Admin może obejść' : 'Blokada bezwzględna'}
              </span>
            </label>
          </div>
        </div>

        {/* Przycisk zapisz */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handleSaveSettings}
            disabled={settingsLoading}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--success)',
              color: 'var(--success)',
              fontWeight: '600',
              opacity: settingsLoading ? 0.7 : 1
            }}
          >
            {settingsLoading ? '⏳ Zapisywanie...' : '💾 Zapisz ustawienia'}
          </button>
          {settingsSaved && (
            <span style={{ color: 'var(--success)', fontWeight: '500' }}>✓ Zapisano!</span>
          )}
        </div>
      </div>

      {/* Sekcja 2: Wyjątki */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '1.25rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h5 style={{
            margin: 0,
            fontSize: '0.95rem',
            fontWeight: '600',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            📋 Wyjątki produktów ({exceptions.length})
          </h5>
          <button
            onClick={() => setShowAddException(true)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--info)',
              color: 'var(--info)',
              fontWeight: '500',
              fontSize: '12px'
            }}
          >
            + Dodaj wyjątek
          </button>
        </div>

        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Produkty na tej liście mają indywidualną granicę marży (różną od globalnej).
          Możesz ustawić 0% aby pozwolić na sprzedaż po cenie zakupu lub ujemną marżę.
        </p>

        {/* Formularz dodawania */}
        {showAddException && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg)',
            borderRadius: '6px',
            marginBottom: '1rem',
            border: '1px solid var(--border)'
          }}>
            <div className="pl-exception-form" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
              {/* Wyszukiwarka produktu */}
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
                  Produkt *
                </label>
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => handleProductSearch(e.target.value)}
                  placeholder="Wpisz nazwę lub EAN..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px'
                  }}
                />
                {/* Wyniki wyszukiwania */}
                {productSearchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 100,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {productSearchResults.map(product => (
                      <div
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        style={{
                          padding: '0.5rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--bg)',
                          fontSize: '0.85rem'
                        }}
                      >
                        <strong>{product.nazwa}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          EAN: {product.ean || '-'} | Cena zakupu: {product.cena_zakupu?.toFixed(2) || '0.00'} zł
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchLoading && (
                  <div style={{ position: 'absolute', right: '10px', top: '50%' }}>⏳</div>
                )}
              </div>

              {/* Min marża */}
              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
                  Min. marża %
                </label>
                <input
                  type="number"
                  min="-100"
                  max="100"
                  step="0.5"
                  value={newException.min_margin_percent}
                  onChange={(e) => setNewException({ ...newException, min_margin_percent: parseFloat(e.target.value) || 0 })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              {/* Powód */}
              <div>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', display: 'block' }}>
                  Powód (opcjonalnie)
                </label>
                <input
                  type="text"
                  value={newException.reason}
                  onChange={(e) => setNewException({ ...newException, reason: e.target.value })}
                  placeholder="np. Promocja"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              {/* Przyciski */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleAddException}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid var(--success)',
                    color: 'var(--success)'
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    setShowAddException(false);
                    setNewException({ product_id: null, product_name: '', min_margin_percent: 0, reason: '' });
                    setProductSearch('');
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    border: '1px solid var(--text-muted)',
                    color: 'var(--text-muted)'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabela wyjątków */}
        {exceptionsLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            ⏳ Ładowanie wyjątków...
          </div>
        ) : exceptions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: 'var(--text-muted)',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px'
          }}>
            Brak wyjątków. Wszystkie produkty używają globalnej granicy marży ({settings.min_margin_percent}%).
          </div>
        ) : (
          <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Produkt</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', width: '100px', whiteSpace: 'nowrap' }}>Min. marża</th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid var(--border)', width: '100px', whiteSpace: 'nowrap' }}>Cena zakupu</th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid var(--border)', width: '100px', whiteSpace: 'nowrap' }}>Cena sprzedaży</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', width: '100px', whiteSpace: 'nowrap' }}>Akt. marża</th>
                  <th className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Powód</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', width: '60px' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((exc, idx) => (
                  <tr key={exc.id} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                      <strong>{exc.product_name}</strong>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        EAN: {exc.product_ean || '-'}
                      </div>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      borderBottom: '1px solid var(--border)',
                      fontWeight: '600',
                      color: exc.min_margin_percent <= 0 ? 'var(--danger)' : 'var(--success)',
                      whiteSpace: 'nowrap'
                    }}>
                      {exc.min_margin_percent}%
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {exc.purchase_price?.toFixed(2) || '0.00'} zł
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {exc.sell_price?.toFixed(2) || '0.00'} zł
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      borderBottom: '1px solid var(--border)',
                      fontWeight: '500',
                      color: exc.current_margin_percent < exc.min_margin_percent ? 'var(--danger)' : 'var(--success)'
                    }}>
                      {exc.current_margin_percent?.toFixed(1) || '0'}%
                      {exc.current_margin_percent < exc.min_margin_percent && (
                        <span title="Aktualna marża poniżej minimalnej!"> ⚠️</span>
                      )}
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      {exc.reason || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                      <button
                        onClick={() => handleDeleteException(exc.id)}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid var(--danger)',
                          color: 'var(--danger)',
                          fontSize: '11px'
                        }}
                        title="Usuń wyjątek"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceLimitsTab;
